/**
 * Prototype.jsx — thin orchestrator (was 1 717 lines, now ~160)
 * All data, primitives, screens & shell live in prototype/ sub-modules.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles } from '../components/CustomIcons';
import './Prototype.css';

/* ── sub-modules ── */
import { UIProvider } from './prototype/shared';
import BootScreen from './prototype/BootScreen';
import RoleSwitcher from './prototype/RoleSwitcher';
import CaseManager from './prototype/caseManager';
import Instructor from './prototype/instructor';
import Employer from './prototype/employer';
import Admin from './prototype/admin';
import { FacilityAdmin, TrainingProvider, SignInScreen } from './prototype/newRoles';
import YouthLearner from './prototype/youthLearner';
import ProfessionalLearner from './prototype/professionalLearner';
import { DailyChallengeModal, SpotlightTutorial, SettingsSheet } from './prototype/engagement';
import { TabletFrame, OrientationToggle, StepIndicator, useLearnerFlow } from './prototype/TabletShell';

/* ── backward-compat re-exports for files that import from '../Prototype' ── */
export { Btn, Chip, Mono, ProgressBar, ScreenHeader, SafeImage } from './prototype/primitives';
export { TabletFrame } from './prototype/TabletShell';

/* ──────────────────────────────────────────────────────────── */

// Module-level flags survive React StrictMode's double-effect invocation.
let __proto_skipRoleWrite = false;
let __proto_skipOrientationWrite = false;
try {
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('assistivos_fresh_reset') === '1') {
    __proto_skipRoleWrite = true;
    __proto_skipOrientationWrite = true;
    sessionStorage.removeItem('assistivos_fresh_reset');
  }
} catch (_e) { /* ignore */ }

const Prototype = () => {
  const [booted, setBooted] = useState(() => {
    try {
      if (localStorage.getItem('replay_boot') === '1') {
        localStorage.removeItem('replay_boot');
        return false;
      }
      return localStorage.getItem('assistivos_booted') === '1';
    } catch (_e) { return false; }
  });
  const [role, setRole] = useState(() => {
    try {
      if (sessionStorage.getItem('assistivos_fresh_reset') === '1') return 'learner';
      const active = localStorage.getItem('assistivos_active_role');
      if (active) return active;
      return localStorage.getItem('assistivos_demo_role') || 'learner';
    } catch (_e) { return 'learner'; }
  });
  const [orientation, setOrientation] = useState(() => {
    try {
      if (sessionStorage.getItem('assistivos_fresh_reset') === '1') return 'portrait';
    } catch (_e) { /* ignore */ }
    try { return localStorage.getItem('assistivos_orientation') || 'portrait'; } catch (_e) { return 'portrait'; }
  });
  const [fadingRole, setFadingRole] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [signInRole, setSignInRole] = useState(null);

  useEffect(() => {
    if (__proto_skipRoleWrite) { __proto_skipRoleWrite = false; return; }
    try {
      const existing = localStorage.getItem('assistivos_active_role');
      if (existing === null && role === 'learner') return;
      localStorage.setItem('assistivos_demo_role', role);
      localStorage.setItem('assistivos_active_role', role);
    } catch (_e) { /* ignore */ }
  }, [role]);

  useEffect(() => {
    if (__proto_skipOrientationWrite) { __proto_skipOrientationWrite = false; return; }
    try {
      const existing = localStorage.getItem('assistivos_orientation');
      if (existing === null && orientation === 'portrait') return;
      localStorage.setItem('assistivos_orientation', orientation);
    } catch (_e) { /* ignore */ }
  }, [orientation]);

  useEffect(() => {
    try {
      const large = localStorage.getItem('assistivos_large_text') === 'true';
      document.querySelector('.proto-root')?.setAttribute('data-large-text', large ? 'true' : 'false');
    } catch (_e) { /* ignore */ }
  }, [booted]);

  const finishBoot = () => {
    try { localStorage.setItem('assistivos_booted', '1'); } catch (_e) { /* ignore */ }
    setBooted(true);
  };

  const switchRole = (next) => {
    if (next === role) return;
    setFadingRole(true);
    setTimeout(() => {
      setRole(next);
      setFadingRole(false);
      if (next !== 'learner') setSignInRole(next);
    }, 200);
  };

  const resetDemo = () => {
    try {
      [
        'aos_prototype_v1', 'assistivos_casemanager_state', 'assistivos_instructor_state',
        'assistivos_employer_state', 'assistivos_admin_state', 'assistivos_booted',
        'assistivos_demo_role', 'assistivos_active_role', 'assistivos_orientation',
        'assistivos_learner_goal', 'assistivos_tutorial_complete', 'assistivos_moods',
        'assistivos_journal_entries', 'assistivos_challenge_answers', 'assistivos_streak',
        'assistivos_xp', 'assistivos_plain_language', 'assistivos_large_text',
        'assistivos_goal_badge_dismissed', 'assistivos_youth_v1', 'assistivos_youth_fx',
        'assistivos_notifications_v1', 'assistivos_fa_approvals_v1', 'assistivos_tp_state_v1',
        'assistivos_fa_state_v1', 'assistivos_admin_prefs_v1', 'assistivos_approvals',
        'assistivos_sidebar_collapsed', 'assistivos_prof_v1',
      ].forEach((k) => localStorage.removeItem(k));
      try { sessionStorage.removeItem('assistivos_pending_role_switch'); } catch (_e) { /* ignore */ }
      try { sessionStorage.setItem('assistivos_fresh_reset', '1'); } catch (_e) { /* ignore */ }
      localStorage.setItem('replay_boot', '1');
    } catch (_e) { /* ignore */ }
    window.location.reload();
  };

  const learner = useLearnerFlow({
    onOpenChallenge: () => setChallengeOpen(true),
    onOpenSettings: () => setSettingsOpen(true),
    onOpenJournal: () => setJournalOpen(true),
    journalOpen,
    onCloseJournal: () => setJournalOpen(false),
  });

  useEffect(() => {
    if (!booted || role !== 'learner') return;
    if (learner.step !== '1B') return;
    try {
      if (localStorage.getItem('assistivos_tutorial_complete') !== 'true') {
        const t = setTimeout(() => setShowTutorial(true), 400);
        return () => clearTimeout(t);
      }
    } catch (_e) { /* ignore */ }
  }, [booted, role, learner.step]);

  let roleContent, railProps = null;
  if (role === 'learner') {
    roleContent = learner.content;
    railProps = learner.railProps;
  } else if (role === 'case_manager') {
    roleContent = <CaseManager />;
  } else if (role === 'instructor') {
    roleContent = <Instructor />;
  } else if (role === 'employer') {
    roleContent = <Employer />;
  } else if (role === 'facility_admin') {
    roleContent = <FacilityAdmin />;
  } else if (role === 'training_provider') {
    roleContent = <TrainingProvider />;
  } else if (role === 'youth_learner') {
    roleContent = <YouthLearner />;
  } else if (role === 'professional_learner') {
    roleContent = <ProfessionalLearner />;
  } else if (role === 'admin') {
    roleContent = <Admin onResetDemo={resetDemo} role={role} />;
  }

  return (
    <UIProvider>
      <div className="proto-root min-h-screen w-full flex flex-col" data-testid="proto-page" data-orientation={orientation}>
        <a
          href="#proto-tablet"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-[#0f2744] focus:text-[#fafaf9] focus:px-3 focus:py-2 focus:rounded"
        >
          Skip to prototype
        </a>

        <header className="w-full px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[12px] flex items-center justify-center" style={{ background: '#f97316' }} aria-hidden="true">
              <Sparkles className="w-4 h-4" style={{ color: '#0f2744' }} />
            </div>
            <div>
              <div className="p-display text-[17px] leading-tight" style={{ color: '#0f2744', margin: 0 }}>AssistivOS</div>
              <div className="text-[12px] font-semibold uppercase tracking-widest" style={{ color: '#64748b', letterSpacing: '0.08em' }}>Interactive Prototype</div>
            </div>
          </div>
          {role === 'learner' && booted && (
            <div className="hidden md:block">
              <StepIndicator step={learner.step} />
            </div>
          )}
          <button
            onClick={() => { if (window.confirm('Reset all demo data? All progress will be cleared.')) resetDemo(); }}
            className="text-[13px] font-semibold px-3 py-2 rounded-[8px]"
            style={{ color: '#64748b' }}
            data-testid="proto-header-reset"
            aria-label="Reset demo progress"
          >
            Reset
          </button>
        </header>

        <div className="px-4 pb-2">
          <RoleSwitcher active={role} onChange={switchRole} disabled={!booted} />
        </div>

        <main id="proto-tablet" className="flex-1 flex flex-col items-center justify-center px-4 py-6">
          <TabletFrame railProps={role === 'learner' ? railProps : null} orientation={orientation}>
            {!booted && <BootScreen onDone={finishBoot} />}
            <div
              style={{ opacity: fadingRole ? 0 : 1, transition: 'opacity 200ms ease-out' }}
              className="flex flex-col h-full"
              data-testid={`proto-role-content-${role}`}
            >
              {roleContent}
            </div>
            {booted && role === 'learner' && showTutorial && (
              <SpotlightTutorial onComplete={() => setShowTutorial(false)} />
            )}
            {booted && role === 'learner' && challengeOpen && (
              <DailyChallengeModal onClose={() => setChallengeOpen(false)} />
            )}
            {booted && role === 'learner' && settingsOpen && (
              <SettingsSheet onClose={() => setSettingsOpen(false)} />
            )}
            {booted && signInRole && (
              <SignInScreen role={signInRole} onContinue={() => setSignInRole(null)} />
            )}
          </TabletFrame>
          {booted && <OrientationToggle orientation={orientation} onChange={setOrientation} />}
        </main>

        <footer className="w-full px-6 py-5 text-center text-[13px] leading-relaxed" style={{ color: '#64748b' }}>
          <div>Powered by <span className="font-semibold" style={{ color: '#0f2744' }}>Tech Tradesmith Institute (TTI)</span> — A program of HDGU Incorporated</div>
          <div className="mt-1" style={{ color: '#94a3b8' }}>info@hdguniversity.org · Plano, TX</div>
        </footer>
      </div>
    </UIProvider>
  );
};

export default Prototype;
